import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Smartphone, Search, Zap } from 'lucide-react';

export default function Landing(){
    const [count, setCount] = useState(0);
    const targetCount = 3842; // Your "3800+" target

    // Fast counter effect on load
    useEffect(() => {
        let start = 0;
        const duration = 2000; // 2 seconds
        const increment = Math.ceil(targetCount / (duration / 16));

        const timer = setInterval(() => {
            start += increment;
            if (start >= targetCount) {
                setCount(targetCount);
                clearInterval(timer);
            } else {
                setCount(start);
            }
        }, 16);

        return () => clearInterval(timer);
    }, []);

    // Floating phones logic (positions and delay)
    const floatingPhones = [
        { id: 1, top: '15%', left: '10%', delay: 0, scale: 1, img: "https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=300" },
        { id: 2, top: '65%', left: '15%', delay: 1, scale: 0.8, img: "https://images.unsplash.com/photo-1592890288564-76628a30a657?w=300" },
        { id: 3, top: '20%', left: '75%', delay: 0.5, scale: 0.9, img: "https://images.unsplash.com/photo-1523206489230-c012c64b2b48?w=300" },
        { id: 4, top: '70%', left: '80%', delay: 1.5, scale: 1.1, img: "https://images.unsplash.com/photo-1556656793-062ff987b483?w=300" },
    ];

    return (
        <div className="relative min-h-screen w-full bg-slate-950 overflow-hidden flex items-center justify-center font-sans">

            {/* Background Decorative Gradient */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-600/20 rounded-full blur-[120px]" />
                <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-purple-600/20 rounded-full blur-[120px]" />
            </div>

            {/* --- FLYING PHONES --- */}
            {floatingPhones.map((phone) => (
                <motion.div
                    key={phone.id}
                    className="absolute z-10 hidden md:block rounded-3xl overflow-hidden border border-white/10 shadow-2xl"
                    style={{ top: phone.top, left: phone.left }}
                    initial={{ y: 0 }}
                    animate={{
                        y: [0, -40, 0],
                        rotate: [0, 5, -5, 0],
                    }}
                    transition={{
                        duration: 6,
                        repeat: Infinity,
                        delay: phone.delay,
                        ease: "easeInOut"
                    }}
                >
                    <img
                        src={phone.img}
                        alt="Phone preview"
                        className="w-32 h-56 object-cover opacity-60 hover:opacity-100 transition-opacity"
                        style={{ transform: `scale(${phone.scale})` }}
                    />
                </motion.div>
            ))}

            {/* --- MAIN CONTENT --- */}
            <div className="relative z-20 text-center px-6 max-w-4xl">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
          <span className="px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-medium mb-6 inline-block">
            Trusted by 50,000+ shoppers
          </span>

                    <h1 className="text-5xl md:text-7xl font-extrabold text-white tracking-tight mb-8">
                        Find the <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500">best price</span> <br />
                        for your next phone
                    </h1>

                    <p className="text-lg text-slate-400 mb-10 max-w-2xl mx-auto leading-relaxed">
                        Compare specs, track prices, and get alerts for the latest smartphones from over 50 global retailers.
                    </p>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="px-8 py-4 bg-white text-slate-950 rounded-2xl font-bold text-lg flex items-center gap-3 shadow-[0_0_20px_rgba(255,255,255,0.3)] transition-all"
                        >
                            Browse {count.toLocaleString()}+ devices
                            <ArrowRight size={20} />
                        </motion.button>

                        <button className="px-8 py-4 bg-slate-900 text-white rounded-2xl font-bold text-lg border border-white/10 hover:bg-slate-800 transition-colors">
                            How it works
                        </button>
                    </div>
                </motion.div>

                {/* Feature Icons */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1, duration: 1 }}
                    className="mt-20 grid grid-cols-2 md:grid-cols-3 gap-8 border-t border-white/5 pt-12"
                >
                    <div className="flex items-center gap-3 text-slate-400 justify-center">
                        <Search size={18} className="text-blue-500" />
                        <span className="text-sm uppercase tracking-widest">Smart Search</span>
                    </div>
                    <div className="flex items-center gap-3 text-slate-400 justify-center">
                        <Zap size={18} className="text-yellow-500" />
                        <span className="text-sm uppercase tracking-widest">Instant Alerts</span>
                    </div>
                    <div className="flex items-center gap-3 text-slate-400 justify-center hidden md:flex">
                        <Smartphone size={18} className="text-purple-500" />
                        <span className="text-sm uppercase tracking-widest">Global Specs</span>
                    </div>
                </motion.div>
            </div>

        </div>
    );
};